/**
 * The readiness checklist, generated from the owner's goal, stage and what the
 * workspace already holds. Pure.
 *
 * Each item says why it matters and what is still missing. Wording describes
 * what lenders commonly ask for; it does not state program rules, which vary
 * by lender and change over time — those become questions in the action plan.
 */

import { coverage } from '../core/financials.js';
import { defaultRatePct } from '../core/estimate.js';
import { findConflicts } from '../documents/reconcile.js';
import type { DocKind, ReadinessStatus, Workspace } from '../workspace/schema.js';

export type ItemGroup = 'request' | 'financials' | 'documents' | 'owner';

export interface ChecklistItem {
  id: string;
  group: ItemGroup;
  title: string;
  why: string;
  status: ReadinessStatus;
  /** What remains, in plain language. Empty when ready. */
  missing: string;
  /** Where in the app to work on it. */
  route?: string;
  /** Set when the status comes from the owner's own override. */
  overridden?: boolean;
  /** Commonly asked for given this goal and stage, vs. helpful. */
  importance: 'likely' | 'helpful';
}

export const GROUP_LABELS: Record<ItemGroup, string> = {
  request: 'Your financing request',
  financials: 'Your numbers',
  documents: 'Documents',
  owner: 'You as the owner',
};

function docStatus(ws: Workspace, kind: DocKind): { status: ReadinessStatus; missing: string } {
  const docs = ws.documents.filter((d) => d.kind === kind);
  if (docs.length === 0) {
    return ws.docsOnHand.includes(kind)
      ? { status: 'needs-review', missing: 'You said you have this — upload it so its figures can be read and checked.' }
      : { status: 'not-started', missing: 'Not gathered yet.' };
  }
  const pending = docs.flatMap((d) => d.candidates).filter((c) => c.state === 'pending').length;
  const unreadable = docs.filter((d) => d.status === 'no-text' || d.status === 'error' || d.status === 'unsupported');
  if (pending > 0) return { status: 'needs-review', missing: `${pending} figure${pending === 1 ? '' : 's'} read from it still need your confirmation.` };
  if (unreadable.length === docs.length) {
    return { status: 'needs-review', missing: 'Uploaded, but no text could be read (it may be a scan). Enter its key figures by hand.' };
  }
  return { status: 'ready', missing: '' };
}

export function buildChecklist(ws: Workspace): ChecklistItem[] {
  const p = ws.profile;
  const goal = p.goal?.value;
  const stage = p.stage?.value;
  const planning = stage === 'planning';
  const acquiring = goal === 'buy-business' || stage === 'acquiring';
  const items: ChecklistItem[] = [];

  // ---- the request
  items.push({
    id: 'goal',
    group: 'request',
    title: 'Financing goal',
    why: 'Everything else — the program, the term, the paperwork — follows from what the money is for.',
    status: !goal ? 'not-started' : goal === 'not-sure' ? 'needs-review' : 'ready',
    missing: !goal ? 'Tell the assistant what you are looking to fund.' : goal === 'not-sure' ? 'Narrow down what the money is for; a lender can help.' : '',
    route: '#/assistant',
    importance: 'likely',
  });
  const hasUse = !!p.fundsDescription?.value || goal === 'buy-business' || goal === 'real-estate' || goal === 'refinance';
  items.push({
    id: 'amount-use',
    group: 'request',
    title: 'Amount and use of funds',
    why: 'Lenders want a specific amount and a clear account of what it pays for.',
    status: p.amount && hasUse ? 'ready' : p.amount || hasUse ? 'needs-review' : 'not-started',
    missing: !p.amount ? 'Set the amount you need.' : !hasUse ? 'Describe what the money will pay for.' : '',
    route: '#/assistant',
    importance: 'likely',
  });
  if (acquiring || goal === 'real-estate') {
    items.push({
      id: 'purchase-price',
      group: 'request',
      title: goal === 'real-estate' ? 'Property price and your down payment' : 'Purchase price and your down payment',
      why: 'The lender compares the loan with the total price to see how much of your own money is going in.',
      status: p.purchasePrice && p.ownerInjection ? 'ready' : p.purchasePrice || p.ownerInjection ? 'needs-review' : 'not-started',
      missing: !p.purchasePrice ? 'Add the price.' : !p.ownerInjection ? 'Add how much of your own money is going in.' : '',
      route: '#/assistant',
      importance: 'likely',
    });
  }

  // ---- numbers
  if (!planning) {
    const conflicts = findConflicts(ws).filter((c) => !c.resolved);
    items.push({
      id: 'figures-consistent',
      group: 'financials',
      title: 'Figures agree across documents',
      why: 'Lenders notice when the same number differs between your P&L and your tax return. Better to explain it before they ask.',
      status: conflicts.length > 0 ? 'needs-review' : ws.documents.length > 0 ? 'ready' : 'not-started',
      missing: conflicts.length > 0
        ? `${conflicts.length} figure${conflicts.length === 1 ? '' : 's'} differ between documents: ${conflicts.map((c) => c.label.toLowerCase()).join(', ')}.`
        : ws.documents.length === 0 ? 'Upload documents to check them against each other.' : '',
      route: '#/documents',
      importance: 'likely',
    });
    const cov = p.amount ? coverage(ws, p.amount.value, p.termMonths?.value ?? 120, defaultRatePct(ws)) : null;
    items.push({
      id: 'coverage',
      group: 'financials',
      title: 'Income covers the new payments',
      why: 'The first question most lenders ask: does what the business earns cover its loan payments with room to spare?',
      status: !cov || (!cov.income && !cov.revenue) ? 'not-started'
        : !cov.ready ? 'needs-review'
          : cov.verdict === 'insufficient' || cov.verdict === 'tight' ? 'needs-review' : 'ready',
      missing: !cov ? 'Set the amount first.'
        : !cov.ready ? `Needs ${cov.needs.join(' and ')}.`
          : cov.verdict === 'insufficient' ? 'On confirmed figures, income does not cover the payments. Try a smaller amount or longer term in Scenarios, and be ready to explain to a lender.'
            : cov.verdict === 'tight' ? 'Coverage is tight. Lenders will look for a clear explanation and may want more room.' : '',
      route: '#/scenarios',
      importance: 'likely',
    });
  }

  // ---- documents
  const docItem = (id: string, kind: DocKind, title: string, why: string, importance: ChecklistItem['importance']) => {
    const s = docStatus(ws, kind);
    items.push({ id, group: 'documents', title, why, status: s.status, missing: s.missing, route: '#/documents', importance });
  };
  if (!planning) {
    docItem('doc-pl', 'pl', 'Recent profit and loss statements', 'Shows what the business earns and spends — the basis of every coverage calculation.', 'likely');
    docItem('doc-tax', 'tax-return', 'Business tax returns', 'Lenders commonly ask for recent years of returns to check figures against.', 'likely');
    docItem('doc-bs', 'balance-sheet', 'Balance sheet', 'Shows what the business owns and owes.', 'likely');
    docItem('doc-bank', 'bank-statement', 'Recent business bank statements', 'Shows actual cash moving through the business.', 'helpful');
  }
  if (planning || goal === 'equipment-expansion' || goal === 'not-sure') {
    docItem('doc-plan', 'business-plan', planning ? 'Business plan with financial projections' : 'Plan or projections for the expansion',
      planning ? 'Without a track record, the plan is what a lender evaluates.' : 'Shows how the investment will pay for itself.',
      planning ? 'likely' : 'helpful');
  }
  if ((p.existingAnnualDebtService?.value ?? 0) > 0 || goal === 'refinance') {
    docItem('doc-debt', 'debt-schedule', 'Schedule of existing business debt', 'Lists each loan, its balance and payment — needed to see total debt and, for refinancing, what qualifies.', 'likely');
  }
  if (acquiring) {
    docItem('doc-seller', 'seller-financials', "Seller's financial statements and tax returns", "When buying a business, the lender judges it largely on the seller's records.", 'likely');
    docItem('doc-loi', 'purchase-agreement', 'Purchase agreement or letter of intent', 'Shows the agreed price and terms.', 'likely');
  }
  if (goal === 'real-estate') {
    docItem('doc-property', 'purchase-agreement', 'Property purchase agreement or details', 'Shows the price, terms and how the property will be used.', 'likely');
  }

  // ---- owner
  docItem('doc-pfs', 'personal-financial-statement', 'Personal financial statement',
    'SBA lenders ask owners for a personal financial statement (SBA Form 413) listing personal assets and debts.', 'likely');
  if (planning || acquiring || goal === 'real-estate') {
    items.push({
      id: 'injection',
      group: 'owner',
      title: 'Your own money going in',
      why: 'Lenders usually expect owners to share the risk. Have the amount and where it comes from ready to show.',
      status: p.ownerInjection ? (p.ownerInjection.value > 0 ? 'ready' : 'needs-review') : 'not-started',
      missing: !p.ownerInjection ? 'Add how much of your own money is going in.' : p.ownerInjection.value <= 0 ? 'No owner money entered — expect a lender to ask about this.' : '',
      route: '#/assistant',
      importance: 'likely',
    });
  }
  items.push({
    id: 'credit',
    group: 'owner',
    title: 'Know your personal credit history',
    why: 'Lenders review owners\' personal credit. Checking your own report first means no surprises. This app never asks for it.',
    status: 'not-started',
    missing: 'Mark this ready once you have reviewed your own credit report.',
    importance: 'likely',
  });

  // Owner overrides win over derived status, and are labelled as such.
  return items.map((it) => {
    const o = ws.checklist[it.id];
    return o?.status ? { ...it, status: o.status, overridden: true, missing: o.status === 'ready' ? '' : it.missing } : it;
  });
}

export function readinessSummary(items: ChecklistItem[]): { ready: number; total: number; needsReview: number; notStarted: number } {
  return {
    ready: items.filter((i) => i.status === 'ready').length,
    total: items.length,
    needsReview: items.filter((i) => i.status === 'needs-review').length,
    notStarted: items.filter((i) => i.status === 'not-started').length,
  };
}

export const STATUS_LABELS: Record<ReadinessStatus, string> = {
  'not-started': 'Not started',
  'needs-review': 'Needs review',
  ready: 'Ready to discuss with a lender',
};
